import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import './POSSettings.css';

const POSSettings = () => {
  const [settings, setSettings] = useState({
    // Bill Format Settings
    showLogo: true,
    showGST: true,
    showFSSAI: true,
    showQRCode: true,
    gstNumber: '',
    fssaiNumber: '',
    upiId: '',
    logoUrl: '',

    // Terminal Settings
    enableMultiTerminal: false,
    masterTerminalId: '',

    // Printer Settings
    defaultPrinterSize: '80mm',
    autoPrintKOT: true,
    autoPrintBill: false,

    // Other Settings
    enableTokenSystem: true,
    taxRate: 5,
    serviceTaxRate: 0
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  // Load settings
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsDoc = await getDoc(doc(db, 'settings', 'posConfig'));
      if (settingsDoc.exists()) {
        setSettings(prev => ({ ...prev, ...settingsDoc.data() }));
        setLogoPreview(settingsDoc.data().logoUrl || '');
      }
    } catch (error) {
      console.error('Error loading POS settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return settings.logoUrl;

    try {
      const logoRef = ref(storage, `pos-settings/logo-${Date.now()}.${logoFile.name.split('.').pop()}`);
      await uploadBytes(logoRef, logoFile);
      const downloadURL = await getDownloadURL(logoRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Upload logo if changed
      let logoUrl = settings.logoUrl;
      if (logoFile) {
        logoUrl = await uploadLogo();
      }

      const updatedSettings = {
        ...settings,
        logoUrl,
        updatedAt: new Date()
      };

      await setDoc(doc(db, 'settings', 'posConfig'), updatedSettings);

      setSettings(updatedSettings);
      setLogoFile(null);
      alert('✅ POS settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('❌ Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pos-settings-page">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="pos-settings-page">
      <div className="page-header">
        <h1>POS Settings</h1>
        <p>Configure your Point of Sale system</p>
      </div>

      <div className="settings-container">
        {/* Bill Format Settings */}
        <div className="settings-card">
          <h2 className="card-title">Bill Format Settings</h2>

          <div className="setting-group">
            <label className="setting-label">Restaurant Logo</label>
            <div className="logo-upload-section">
              {logoPreview && (
                <div className="logo-preview">
                  <img src={logoPreview} alt="Logo Preview" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="file-input"
                id="logo-upload"
              />
              <label htmlFor="logo-upload" className="file-label">
                📁 Choose Logo
              </label>
            </div>
          </div>

          <div className="setting-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.showLogo}
                onChange={(e) => handleChange('showLogo', e.target.checked)}
              />
              <span>Show Logo on Bill</span>
            </label>
          </div>

          <div className="setting-group">
            <label className="setting-label">GST Number</label>
            <input
              type="text"
              value={settings.gstNumber}
              onChange={(e) => handleChange('gstNumber', e.target.value)}
              placeholder="GST-123456WERTYU"
              className="setting-input"
            />
          </div>

          <div className="setting-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.showGST}
                onChange={(e) => handleChange('showGST', e.target.checked)}
              />
              <span>Show GST Number on Bill</span>
            </label>
          </div>

          <div className="setting-group">
            <label className="setting-label">FSSAI Number</label>
            <input
              type="text"
              value={settings.fssaiNumber}
              onChange={(e) => handleChange('fssaiNumber', e.target.value)}
              placeholder="FSSAI-ABC123ARPIT"
              className="setting-input"
            />
          </div>

          <div className="setting-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.showFSSAI}
                onChange={(e) => handleChange('showFSSAI', e.target.checked)}
              />
              <span>Show FSSAI Number on Bill</span>
            </label>
          </div>

          <div className="setting-group">
            <label className="setting-label">UPI ID (for QR Code)</label>
            <input
              type="text"
              value={settings.upiId}
              onChange={(e) => handleChange('upiId', e.target.value)}
              placeholder="merchant@upi"
              className="setting-input"
            />
          </div>

          <div className="setting-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.showQRCode}
                onChange={(e) => handleChange('showQRCode', e.target.checked)}
              />
              <span>Show UPI QR Code on Bill</span>
            </label>
          </div>
        </div>

        {/* Terminal Settings */}
        <div className="settings-card">
          <h2 className="card-title">Terminal Settings</h2>

          <div className="setting-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.enableMultiTerminal}
                onChange={(e) => handleChange('enableMultiTerminal', e.target.checked)}
              />
              <span>Enable Multi-Terminal Billing</span>
            </label>
            <p className="setting-hint">
              Allow multiple billing counters to sync with master POS
            </p>
          </div>

          {settings.enableMultiTerminal && (
            <div className="setting-group">
              <label className="setting-label">Master Terminal ID</label>
              <input
                type="text"
                value={settings.masterTerminalId}
                onChange={(e) => handleChange('masterTerminalId', e.target.value)}
                placeholder="MASTER-001"
                className="setting-input"
              />
              <p className="setting-hint">
                ID of the master station that controls all counters
              </p>
            </div>
          )}
        </div>

        {/* Printer Settings */}
        <div className="settings-card">
          <h2 className="card-title">Printer Settings</h2>

          <div className="setting-group">
            <label className="setting-label">Default Printer Size</label>
            <select
              value={settings.defaultPrinterSize}
              onChange={(e) => handleChange('defaultPrinterSize', e.target.value)}
              className="setting-select"
            >
              <option value="58mm">58mm (Thermal)</option>
              <option value="80mm">80mm (Thermal)</option>
              <option value="A4">A4 (Laser)</option>
            </select>
          </div>

          <div className="setting-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.autoPrintKOT}
                onChange={(e) => handleChange('autoPrintKOT', e.target.checked)}
              />
              <span>Auto Print KOT</span>
            </label>
            <p className="setting-hint">
              Automatically print Kitchen Order Ticket when order is placed
            </p>
          </div>

          <div className="setting-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.autoPrintBill}
                onChange={(e) => handleChange('autoPrintBill', e.target.checked)}
              />
              <span>Auto Print Bill</span>
            </label>
            <p className="setting-hint">
              Automatically print bill when order is completed
            </p>
          </div>
        </div>

        {/* Tax & Other Settings */}
        <div className="settings-card">
          <h2 className="card-title">Tax & Other Settings</h2>

          <div className="setting-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.enableTokenSystem}
                onChange={(e) => handleChange('enableTokenSystem', e.target.checked)}
              />
              <span>Enable Token System</span>
            </label>
            <p className="setting-hint">
              Use token numbers for order tracking
            </p>
          </div>

          <div className="setting-group">
            <label className="setting-label">Tax Rate (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={settings.taxRate}
              onChange={(e) => handleChange('taxRate', parseFloat(e.target.value) || 0)}
              className="setting-input"
            />
          </div>

          <div className="setting-group">
            <label className="setting-label">Service Tax Rate (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={settings.serviceTaxRate}
              onChange={(e) => handleChange('serviceTaxRate', parseFloat(e.target.value) || 0)}
              className="setting-input"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="settings-footer">
        <button
          className="btn-save-settings"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '💾 Saving...' : '💾 Save All Settings'}
        </button>
      </div>
    </div>
  );
};

export default POSSettings;
